import type { User } from '@supabase/supabase-js';

import { CLIENT_VERSION_INFO } from './version-client';

type BetterStackUserPayload = {
  id: string
  email?: string
  username?: string
  provider?: string
  plan?: string
};

type BetterStackQueueEntry = unknown[];

type BetterStackFunction = ((...args: unknown[]) => void) & {
  q?: BetterStackQueueEntry[]
  l?: number
};

declare global {
  interface Window {
    betterstack?: BetterStackFunction
  }
}

const PLACEHOLDER_TOKEN_PATTERNS = [
  /^your-(dev-)?better-stack-js-tag-token$/i,
  /^your-(dev-)?betterstack-js-tag-token$/i,
];

const trimString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

export const resolveBetterStackJsTagToken = (raw?: string | null): string | undefined => {
  const trimmed = trimString(raw);
  if (!trimmed) {
    return undefined;
  }
  if (PLACEHOLDER_TOKEN_PATTERNS.some((pattern) => pattern.test(trimmed))) {
    return undefined;
  }
  return trimmed;
};

const escapeForInlineScript = (value: string) =>
  value.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/</g, '\\u003c');

/**
 * Returns the inline `<script>` body that loads the Better Stack JavaScript tag
 * (errors + session replays + web events + web vitals) if a token is configured.
 *
 * Returns `null` when no token is set, so we never render an empty script tag.
 */
export const buildBetterStackTagScript = (): string | null => {
  const token = resolveBetterStackJsTagToken(process.env.NEXT_PUBLIC_BETTER_STACK_JS_TAG_TOKEN);
  if (!token) {
    return null;
  }

  const environment = trimString(process.env.NEXT_PUBLIC_BETTER_STACK_ENVIRONMENT)
    ?? CLIENT_VERSION_INFO.environment
    ?? 'production';

  const release = trimString(CLIENT_VERSION_INFO.gitSha) ?? trimString(CLIENT_VERSION_INFO.version);

  const initOptions: Record<string, string> = {
    environment,
  };
  if (release) {
    initOptions.release = release;
  }

  const initOptionsLiteral = Object.entries(initOptions)
    .map(([key, value]) => `${key}:'${escapeForInlineScript(value)}'`)
    .join(',');

  return [
    "!function(b,e,t,r){",
    "b[t]=b[t]||function(){(b[t].q=b[t].q||[]).push(arguments)};",
    "b[t].l=+new Date;",
    "var s=e.createElement('script');s.async=1;s.crossOrigin='anonymous';",
    "s.src='https://betterstack.net/b.js?t='+r;",
    "(e.head||e.getElementsByTagName('head')[0]).appendChild(s);",
    `}(window,document,'betterstack','${escapeForInlineScript(token)}');`,
    `betterstack('init',{${initOptionsLiteral}});`,
  ].join('');
};

const resolveUsername = (user: User): string => {
  const meta = (user.user_metadata ?? {}) as Record<string, unknown>;
  return (
    trimString(meta.full_name)
    ?? trimString(meta.name)
    ?? trimString(meta.preferred_username)
    ?? trimString(user.email)
    ?? user.id
  );
};

export const buildBetterStackUserPayload = (
  user: User,
  plan?: string | null,
): BetterStackUserPayload => {
  const payload: BetterStackUserPayload = {
    id: user.id,
    username: resolveUsername(user),
  };

  const email = trimString(user.email);
  if (email) {
    payload.email = email;
  }

  const provider = trimString((user.app_metadata as Record<string, unknown> | undefined)?.provider);
  if (provider) {
    payload.provider = provider;
  }

  const planValue = trimString(plan);
  if (planValue) {
    payload.plan = planValue;
  }

  return payload;
};

// Module-level identity state so plan updates (which arrive separately from
// the auth event, via the subscription/scan-status fetch) can be merged into
// the same Better Stack `user` identity. The `user` command replaces the whole
// identity each call, so we always re-emit the full payload.
let identifiedUser: User | null = null;
let identifiedPlan: string | undefined;

const emitBetterStackUser = (): void => {
  if (typeof window === 'undefined') {
    return;
  }
  const fn = window.betterstack;
  if (!fn) {
    return;
  }

  if (identifiedUser) {
    fn('user', buildBetterStackUserPayload(identifiedUser, identifiedPlan));
  } else {
    fn('user', null);
  }
};

/**
 * Syncs the identified user with the Better Stack JS tag. Safe to call on
 * server (no-op) or before the tag has finished loading - the snippet queues
 * commands until the remote `b.js` initializes.
 */
export const syncBetterStackUser = (user: User | null | undefined): void => {
  identifiedUser = user ?? null;
  if (!identifiedUser) {
    // Clear plan on logout so the next identity starts clean.
    identifiedPlan = undefined;
  }
  emitBetterStackUser();
};

/**
 * Associates a subscription plan (e.g. `free` / `pro`) with the currently
 * identified Better Stack user. No-ops when no user is identified yet; the
 * plan is remembered and applied on the next `syncBetterStackUser` call.
 */
export const setBetterStackUserPlan = (plan: string | null | undefined): void => {
  const normalized = trimString(plan);
  if (identifiedPlan === normalized) {
    return;
  }
  identifiedPlan = normalized;
  if (identifiedUser) {
    emitBetterStackUser();
  }
};
